/**
 * Gravação e reexecução de replay — §14.2 e §14.4.
 *
 * O replay é o que permite o ranking de season existir sem confiar no cliente:
 * a run inteira viaja como um log de inputs, e o servidor a reexecuta. Uma run
 * de 5 minutos são 18.000 ticks; com 1 byte por tick e codificação por
 * repetição, o envio fica na casa de poucos KB.
 */
import { MAX_REPLAY_TICKS } from './config.js';
import { Sim } from './sim/sim.js';
import { NEUTRAL_INPUT, packInput, unpackInput } from './types.js';
/** Grava inputs tick a tick, comprimindo repetições na hora. */
export class ReplayRecorder {
    runs = [];
    tickCount = 0;
    record(input) {
        const byte = packInput(input);
        const last = this.runs[this.runs.length - 1];
        // Num shooter, o jogador segura a direção por dezenas de ticks: a
        // codificação por repetição derruba o log em uma ordem de grandeza.
        if (last !== undefined && last[0] === byte && last[1] < 0xffff) {
            last[1]++;
        }
        else {
            this.runs.push([byte, 1]);
        }
        this.tickCount++;
    }
    finish(header, claim) {
        return {
            header,
            runs: this.runs.map((run) => [run[0], run[1]]),
            tickCount: this.tickCount,
            claim,
        };
    }
    /** Tamanho serializado estimado: 4 bytes por par [byte, repetições]. */
    get estimatedBytes() {
        return this.runs.length * 4;
    }
}
/** Expande o log comprimido de volta para um input por tick. */
export function* replayInputs(replay) {
    for (const [byte, repeats] of replay.runs) {
        const input = unpackInput(byte);
        for (let i = 0; i < repeats; i++)
            yield input;
    }
}
/**
 * Reexecuta um replay e confere o resultado — o coração do anti-cheat (§14.4).
 *
 * Roda no servidor, em Node, com o mesmo `core` que rodou no navegador. É por
 * isso que §5.3 é inegociável: qualquer divergência entre motores transforma
 * run legítima em `rejected`.
 */
export function verifyReplay(replay, currentRulesVersion) {
    const empty = { score: 0, distance: 0, checksum: 0 };
    if (replay.header.rulesVersion !== currentRulesVersion) {
        return {
            status: 'archived',
            reason: `rulesVersion ${replay.header.rulesVersion} != ${currentRulesVersion}`,
            actual: empty,
            tick: 0,
        };
    }
    if (replay.tickCount > MAX_REPLAY_TICKS) {
        return { status: 'rejected', reason: 'run mais longa que o teto', actual: empty, tick: replay.tickCount };
    }
    const sim = new Sim({
        world: replay.header.world,
        mode: replay.header.mode,
        playerCount: replay.header.playerCount,
    });
    const inputs = new Array(replay.header.playerCount).fill(NEUTRAL_INPUT);
    let ticks = 0;
    for (const input of replayInputs(replay)) {
        inputs[0] = input;
        sim.step(inputs);
        ticks++;
    }
    const actual = {
        score: sim.totalScore,
        distance: sim.scrollDist,
        checksum: sim.checksum(),
    };
    if (ticks !== replay.tickCount) {
        return { status: 'rejected', reason: 'contagem de ticks divergente', actual, tick: ticks };
    }
    if (actual.score !== replay.claim.score) {
        return {
            status: 'rejected',
            reason: `score alegado ${replay.claim.score} != ${actual.score} reexecutado`,
            actual,
            tick: ticks,
        };
    }
    if (actual.distance !== replay.claim.distance) {
        return { status: 'rejected', reason: 'distância divergente', actual, tick: ticks };
    }
    if (actual.checksum !== replay.claim.checksum) {
        return { status: 'rejected', reason: 'checksum divergente', actual, tick: ticks };
    }
    return { status: 'verified', actual, tick: ticks };
}
//# sourceMappingURL=replay.js.map